import "./ticketsKanban.scss";
import { Card, CardContent } from "@mui/material";
import { useState } from "react";
import { queryClient } from "queryClient";
import { FaBuilding, FaCalendarAlt, FaUser } from "react-icons/fa";
import { toast } from "react-toastify";
import { toDate } from "helpers/dateHelper";
import {
  Ticket,
  TicketStatus,
  isTicketTransitionAllowed,
} from "interfaces/entities/ticket.model";
import { useTranslation } from "react-i18next";
import ticketsService from "services/admin/ticketsService";

const statuses = Object.values(TicketStatus);

interface TicketsKanbanViewProps {
  tickets: Ticket[];
  onTicketClick: (ticket: Ticket) => void;
}

export const TicketsKanbanView = ({ tickets, onTicketClick }: TicketsKanbanViewProps) => {
  const { t } = useTranslation();
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);
  const [draggedTicketID, setDraggedTicketID] = useState<any>(null);
  const [draggedFromStatus, setDraggedFromStatus] = useState<TicketStatus | null>(null);

  // CLOSED — финальное состояние, перетаскивать оттуда нельзя (нужно явное переоткрытие).
  const isDraggable = (ticket: Ticket) => ticket.status !== TicketStatus.CLOSED;

  // Переход допустим, если он есть в карте TICKET_STATUS_TRANSITIONS.
  // Закрытие через drag-drop запрещено: requires reason, делается из формы.
  const isDropAllowed = (target: TicketStatus) => {
    if (!draggedFromStatus) return false;
    if (target === TicketStatus.CLOSED) return false;
    return isTicketTransitionAllowed(draggedFromStatus, target);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, ticket: Ticket) => {
    if (!isDraggable(ticket)) {
      e.preventDefault();
      return;
    }
    setDraggedTicketID(ticket?.id);
    setDraggedFromStatus(ticket.status as TicketStatus);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({ ticketID: ticket?.id, status: ticket?.status }),
    );
    e.dataTransfer.setData("text/plain", ticket?.id?.toString() || "");
  };

  const handleDragEnd = () => {
    setDraggedTicketID(null);
    setDraggedFromStatus(null);
    setDragOverStatus(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, status: TicketStatus) => {
    if (!isDropAllowed(status)) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStatus(status);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, status: TicketStatus) => {
    if (!isDropAllowed(status)) return;
    e.preventDefault();
    setDragOverStatus(status);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverStatus(null);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, status: TicketStatus) => {
    e.preventDefault();
    setDragOverStatus(null);
    const ticketID = e.dataTransfer.getData("text/plain");

    const ticket = tickets?.find((t: Ticket) => t.id === Number(ticketID));
    if (!ticket) return;

    if (ticket.status === status) return;

    if (!isDropAllowed(status)) {
      toast.warn(
        t("ticket.transitionNotAllowed", {
          from: t(`ticket.status.${ticket.status}`),
          to: t(`ticket.status.${status}`),
        }),
      );
      return;
    }

    try {
      await ticketsService.patch(ticket.id, { status } as any);

      queryClient.setQueriesData({ queryKey: ["tickets"] }, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          rows: (old.rows || []).map((t: Ticket) =>
            t.id === ticket.id ? { ...t, status } : t,
          ),
        };
      });
      queryClient.invalidateQueries({ queryKey: ["complaints-stats"] });
      queryClient.invalidateQueries({ queryKey: ["tickets-topics-stats"] });
      queryClient.invalidateQueries({ queryKey: ["branches-anti-rating"] });
      queryClient.invalidateQueries({ queryKey: ["trips-anti-rating"] });
    } catch (err: any) {
      const msg = err?.response?.data?.message || t("ticket.statusUpdateError");
      toast.error(msg);
    }
  };

  return (
    <div className="board-container">
      {statuses.map((status: TicketStatus) => {
        const dropAllowed = isDropAllowed(status);
        const dragging = Boolean(draggedFromStatus);
        const columnClasses = [
          "column-content",
          dragOverStatus === status ? "drag-over" : "",
          dragging && !dropAllowed ? "drop-disabled" : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <div key={status} className="column">
            <div className="column-header">
              <span className="text-md">{t(`ticket.status.${status}`)}</span>
              <span className="text-sm">
                ({tickets?.filter((t: Ticket) => t.status === status)?.length || 0})
              </span>
            </div>
            <div
              className={columnClasses}
              onDragOver={(e) => handleDragOver(e, status)}
              onDragEnter={(e) => handleDragEnter(e, status)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, status)}
            >
              {tickets
                ?.filter((t: Ticket) => t.status === status)
                ?.map((ticket: Ticket) => (
                  <div
                    className={`card-wrapper ${draggedTicketID === ticket.id ? "dragging" : ""} ${
                      !isDraggable(ticket) ? "not-draggable" : ""
                    }`}
                    key={ticket.id}
                    draggable={isDraggable(ticket)}
                    onDragStart={(e) => handleDragStart(e, ticket)}
                    onDragEnd={handleDragEnd}
                  >
                    <Card>
                      <CardContent>
                        <div onClick={() => onTicketClick(ticket)}>
                          <div className="card-title-item flex items-center gap-1 mb-2">
                            <span className="card-title-item-value text-sm font-bold">
                              {ticket.subject} #{ticket.id}
                            </span>
                          </div>
                          <div className="card-title-item flex items-center gap-1">
                            <FaCalendarAlt size={12} />
                            <span className="card-title-item-value text-sm">
                              {toDate(ticket.createdAt)}
                            </span>
                          </div>
                          <div className="card-title-item flex items-center gap-1">
                            <FaBuilding size={12} />
                            <span className="card-title-item-value text-sm">
                              {ticket.branch?.name || ticket.branchID || "-"}
                            </span>
                          </div>
                          <div className="card-title-item flex items-center gap-1">
                            <FaUser size={12} />
                            <span className="card-title-item-value text-sm">
                              {ticket.member?.name || ticket.memberID || "-"}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
